import React, {useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {getModelForResourceType} from 'resources/modelRegistry';
import {Link} from 'react-router';
import {Heading, LoadingSpinner, Pagination, TabNavigation, Text} from '@albert-io/atomic';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import appStore from 'client/AppStore';
import {pushQueryParams} from 'client/history';

import {CleverSyncButton, CleverSyncButtonWithModal} from 'client/Clever/CleverSync';

import {
  CleverSyncContext,
  CleverSyncDispatch,
  CleverSyncReducer
} from 'client/Clever/CleverSyncProvider';

import {getUserQuery} from 'client/Clever/util';

import masqueradeStore from 'generic/Masquerade/Masquerade.store';

import {AddClassButton} from '../NewClassroomModal/NewClassroomModal.react';

import TeacherClassesCard from './TeacherClassesCard/TeacherClassesCard.react';
import ArchivedClassesEmptyState from './EmptyStates/ArchivedClassesEmptyState.react';
import ActiveClassesEmptyState from './EmptyStates/ActiveClassesEmptyState.react';
import GetStartedWithAlbert from './GetStartedWithAlbert/GetStartedWithAlbert.react';
import {getClassroomsForTeacherClassroomsDashboardQuery} from './helpers';
import './teacher-classes.scss';

const ClassroomCards = ({classrooms}) => {
  return (
    <ul className='teacher-classes__classroom-cards-container u-mar-t_4 u-pad-l_0'>
      {classrooms.map((classroom) => {
        const isArchived = appStore.routerProps.location.pathname.includes('/archived');
        const countOfPendingInvitations = isArchived ? 0 : classroom.getClassroomInvitations().size;
        const countOfStudents = classroom.getStudents().size;
        const key = `${classroom.getId()}-${countOfPendingInvitations}-
          ${countOfStudents}`;
        return (
          <li key={key}>
            <TeacherClassesCard
              classroom={classroom}
              countOfPendingInvitations={countOfPendingInvitations}
              isArchived={isArchived}
              countOfStudents={countOfStudents}
            />
          </li>
        );
      })}
    </ul>
  );
};

ClassroomCards.propTypes = {
  classrooms: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(getModelForResourceType('classroom_v1'))
  )
};

const EmptyClassroomsWithCleverSync = () => {
  return (
    <div className='teacher-classes__empty-state u-mar-t_6 u-pad_6 u-border-radius_1 u-border-color_slate-300 u-border u-box-shadow_default'>
      <div className='u-display_flex u-flex-direction_column u-align-items_center u-gap_2'>
        <div className='u-mar-b_1'>Sync your Clever classrooms and roster with Albert.</div>
        <CleverSyncButtonWithModal />
        <Text className='u-mar-tb_1'>Or</Text>
        <AddClassButton label='New Class With Albert' />
      </div>
    </div>
  );
};

const PaginatedClassroomCards = awaitMandarkQueries(
  ({location}) => {
    const {page = 1} = location.query;
    return {
      page,
      queries: {
        classrooms: getClassroomsForTeacherClassroomsDashboardQuery({page})
      },
      loadingElement: <LoadingSpinner className='u-mar-tb_4' size={3} />
    };
  },
  ({classrooms, location, hideGettingStarted, closeGettingStarted, showSync}) => {
    // empty state
    if (classrooms.size === 0) {
      if (appStore.routerProps.location.pathname.includes('/archived')) {
        return <ArchivedClassesEmptyState />;
      }
      if (showSync) {
        return <EmptyClassroomsWithCleverSync />;
      }
      return <ActiveClassesEmptyState />;
    }

    // populated list
    const {page = 1} = location.query;
    const totalPages = getClassroomsForTeacherClassroomsDashboardQuery()
      .getResourceMetadata()
      .getIn(['page', 'total_pages'], 1);

    useEffect(() => {
      if (page > totalPages) {
        pushQueryParams({page: totalPages});
      } else if (page < 1) {
        pushQueryParams({page: 1});
      }
    }, [page, totalPages]);

    return (
      <>
        <div className='teacher-classes__class-cards-get-started-container'>
          <div className='u-display_flex u-flex-direction_column'>
            <ClassroomCards classrooms={classrooms} />
            {totalPages > 1 && (
              <Pagination
                className='teacher-classes__pagination u-mar-t_4 u-mar-b_4'
                defaultPage={page}
                key={`${page}+${totalPages}`}
                onChange={(nextPage) => pushQueryParams({page: nextPage})}
                totalPages={totalPages}
              />
            )}
          </div>
          {!hideGettingStarted && <GetStartedWithAlbert handleClose={closeGettingStarted} />}
        </div>
      </>
    );
  }
);

const Classes = (routerProps) => (
  <div className='teacher-classes'>
    <div className='u-display_flex u-align-items_center u-flex-wrap_wrap u-gap_1 u-mar-b_4'>
      <Heading as='h1' size='xl' className='teacher-classes__header u-flex-grow_1 u-mar-b_0'>
        Your Classes
      </Heading>
      <div className='teacher-classes-buttons'>
        <AddClassButton handleDefaultOpen />
      </div>
    </div>
    <TabNavigation align={appStore.isMobileView() ? 'center' : 'left'} paddingLevel={0}>
      <TabNavigation.Tab
        isActive={!routerProps.location.pathname.includes('/archived')}
        as={Link}
        to='/classes'
      >
        Active
      </TabNavigation.Tab>
      <TabNavigation.Tab
        isActive={routerProps.location.pathname.includes('/archived')}
        as={Link}
        to='/classes/archived'
      >
        Archived
      </TabNavigation.Tab>
    </TabNavigation>
    <PaginatedClassroomCards {...routerProps} showSync={false} />
  </div>
);

const ClassesWithSync = awaitMandarkQueries(
  () => ({
    queries: {
      user: getUserQuery()
    }
  }),
  (routerProps) => {
    const {user} = routerProps;
    const showSync = user.getMeta().isAllowCleverSync();
    const currentSyncId = user.getMeta().getCurrentCleverSyncId();
    const initialState = {
      lastSyncDate: user.getMeta().getLastCleverSyncDate(),
      currentlyInProgressSyncId: currentSyncId,
      userCleverDistrictId: user.getDistrictCleverId(),
      userCleverId: user.getCleverId()
    };
    const [state, dispatch] = useReducer(CleverSyncReducer, initialState);

    return (
      <div className='teacher-classes'>
        <CleverSyncContext.Provider value={state}>
          <CleverSyncDispatch.Provider value={dispatch}>
            <div className='u-display_flex u-align-items_center u-flex-wrap_wrap u-gap_1 u-mar-b_4'>
              <Heading
                as='h1'
                size='xl'
                className='teacher-classes__header u-flex-grow_1 u-mar-b_0'
              >
                Your Classes
              </Heading>
              <div className='teacher-classes-buttons'>
                {showSync && <CleverSyncButton variant='outlined' />}
                <AddClassButton handleDefaultOpen />
              </div>
            </div>
            <TabNavigation align={appStore.isMobileView() ? 'center' : 'left'} paddingLevel={0}>
              <TabNavigation.Tab
                isActive={!routerProps.location.pathname.includes('/archived')}
                as={Link}
                to='/classes'
              >
                Active
              </TabNavigation.Tab>
              <TabNavigation.Tab
                isActive={routerProps.location.pathname.includes('/archived')}
                as={Link}
                to='/classes/archived'
              >
                Archived
              </TabNavigation.Tab>
            </TabNavigation>
            <PaginatedClassroomCards {...routerProps} showSync={showSync} />
          </CleverSyncDispatch.Provider>
        </CleverSyncContext.Provider>
      </div>
    );
  }
);

const TeacherClasses = (routerProps) => {
  if (masqueradeStore.isMasquerading()) {
    return <Classes {...routerProps} />;
  }

  return <ClassesWithSync {...routerProps} />;
};

export default TeacherClasses;
